import { useCallback } from 'react';
import { Annotation } from '../../types/graphqlTypes';
import { DxfExportTypes, useExportFloorsDXF } from './useExportDXF';
import { IfcExportTypes, useExportFloorsIFC } from './useExportIFC';

export enum ObjectExportFormat {
  DXF = 'DXF',
  IFC = 'IFC',
}

// Helper to check if an annotation type can be exported in a given format
export const canExportType = (type: string, format: ObjectExportFormat): boolean => {
  if (format === ObjectExportFormat.DXF) {
    return type in DxfExportTypes;
  } else if (format === ObjectExportFormat.IFC) {
    return type in IfcExportTypes;
  }
  return false;
};

// Helper to check if an annotation type can be exported in any format
export const canExportInAnyFormat = (type: string): boolean => {
  return Object.values(ObjectExportFormat).some((format) => canExportType(type, format));
};

export interface FloorsExportInput {
  /** Exportable objects of the as-built floors. */
  objects: Annotation[];
  /** As-built floors exporting as full walls (IFC only). */
  fullWallsFloors?: Set<string>;
  /** Standardised floors; export their layout. */
  layoutFloorIds: string[];
  /** DXF only: labels for the objects. */
  includeLabels?: boolean;
}

// Objects + layouts merged into one file per format.
export const useExportFloors = () => {
  const { onExportFloorsDXF } = useExportFloorsDXF();
  const { onExportFloorsIFC } = useExportFloorsIFC();

  const exportFloors = useCallback(
    (input: FloorsExportInput, format: ObjectExportFormat) => {
      if (format === ObjectExportFormat.DXF) {
        onExportFloorsDXF({ objects: input.objects, includeLabels: input.includeLabels, layoutFloorIds: input.layoutFloorIds });
      } else if (format === ObjectExportFormat.IFC) {
        onExportFloorsIFC({ objects: input.objects, fullWallsFloors: input.fullWallsFloors, layoutFloorIds: input.layoutFloorIds });
      }
    },
    [onExportFloorsDXF, onExportFloorsIFC],
  );

  return { exportFloors };
};
